import { useQuery } from "@tanstack/react-query";
import { v2Client } from "@/utils/axios";
import { getCurrentAuth } from "@/hooks/useAxiosPrivate";
import { APIRoutes } from "@/utils/constants";

/**
 * The editorial write-up attached to a game, as `/getGameSummaryByGameId`
 * returns it.
 *
 * ONE RECORD HOLDS BOTH HALVES: the pre-game preview and the post-game recap
 * live on the same row, each with a long form and a short one. The watch page's
 * sidebar already reads this endpoint for the recap (VideoPlayerSideBar); the
 * countdown screen reads the same row for the preview, so a game written once
 * reads the same on both screens.
 *
 * Every field is optional. Most games have no write-up at all, which is why the
 * preview card is the last thing on the countdown screen and renders nothing
 * when these come back empty.
 */
export type GameSummary = {
  Title?: string | null;
  PreGameSummary?: string | null;
  PreGameShortSummary?: string | null;
  PostGameSummary?: string | null;
  PostGameShortSummary?: string | null;
};

function authHeaders(): Record<string, string> {
  const token = getCurrentAuth()?.AccessToken;
  return token ? { Authorization: `bearer ${token}` } : {};
}

/**
 * The write-up for one game, fetched only when asked for.
 *
 * A 404 here is the normal case, not a failure: the row only exists for games
 * an editor has written. It resolves to null rather than retrying, so a
 * scheduled game with no preview costs one request and no error toast.
 */
export function useGameSummary(gameId?: string | number | null, enabled = true) {
  const id = gameId == null || gameId === "" ? null : String(gameId);

  return useQuery({
    queryKey: ["v2", "watch", "game-summary", id],
    enabled: Boolean(id) && enabled,
    retry: false,
    staleTime: 5 * 60 * 1000,
    queryFn: async ({ signal }) => {
      try {
        const res = await v2Client.get<{ data: GameSummary | null }>(
          APIRoutes.GetGameSummaryByGameId.replace(":gameId", String(id)),
          { headers: authHeaders(), signal },
        );
        return res.data?.data ?? null;
      } catch {
        return null;
      }
    },
  });
}
